import {Capacitor, FilesystemDirectory, FilesystemEncoding, Plugins} from '@capacitor/core';
import {Injectable} from '@angular/core';
import SewingPatternInterface from '../../models/sewing-pattern.interface';
import {Storage} from '@ionic/storage';
import SewingPatternProject from '../../models/sewing-pattern-project.model';
import {ulid} from 'ulid';
import {PatternTransformService} from '../pattern/pattern-transform.service';
import {DomSanitizer} from '@angular/platform-browser';
import FabricInterface from '../../models/fabric.interface';
import PatternAreaInterface from '../../models/pattern-area.interface';
import {tar} from "@ionic/cli/lib/utils/archive";

const { Filesystem } = Plugins;

@Injectable({
    providedIn: 'root'
})
export class ConfiguratorService {
    static savedPatternsStorageKey = 'savedSewingPatterns2021_03';
    private patternPrefix = 'pattern-';

    subscriptions = [];

    constructor(private storage: Storage,
                private patternTransformService: PatternTransformService,

                private domSanitizer: DomSanitizer) {
    }

    async subscribeSewingPatterns(callback: any) {
        this.subscriptions.push(callback);

        const projects = await this.getSavedSewingPatterns();
        callback(projects);

        return () => {
            this.subscriptions = this.subscriptions.filter(subscription => subscription !== callback);
        };
    }

    async informSubscribers() {
        const sewingPatterns = await this.getSavedSewingPatterns();
        this.subscriptions.forEach(subscription => subscription(sewingPatterns));
    }

    async loadImageFromProject(sewingPatternProject: SewingPatternProject) {
        console.log("Loading image", sewingPatternProject);
        try {
            const result = await Filesystem.readFile({
                path: `configurator/${sewingPatternProject.id}.svg`,
                directory: FilesystemDirectory.Data,
                encoding: FilesystemEncoding.UTF8
            });

            console.log(result);

            return result.data;
        } catch (error) {
            console.log(sewingPatternProject.id);
            console.error(error);
        }
    }

    async getSavedSewingPatterns(): Promise<SewingPatternProject[]> {

        let savedPatternsString: string;
        try {
            await this.storage.ready();
            savedPatternsString = await this.storage.get(ConfiguratorService.savedPatternsStorageKey);
        } catch (error) {
            savedPatternsString = localStorage.get(ConfiguratorService.savedPatternsStorageKey);
        }

        if (!savedPatternsString || savedPatternsString === '') {
            return [];
        }

        return (await Promise.all(JSON.parse(savedPatternsString)
            .map(async (project: SewingPatternProject) => {
                project.url = this.domSanitizer.bypassSecurityTrustUrl(
                    Capacitor.convertFileSrc((await this.loadPngFromSewingPatternProject(project)) + `${project.updatedAt}`)
                );
                return project;
            })))
            .sort((a: SewingPatternProject, b: SewingPatternProject) => b.updatedAt - a.updatedAt) as SewingPatternProject[];
    }

    async removeSewingPatternProject(sewingPatternProject: SewingPatternProject) {
        await Filesystem.deleteFile({
            path: `configurator/${sewingPatternProject.id}.svg`,
            directory: FilesystemDirectory.Data,
        });

        let savedProjects = await this.getSavedSewingPatterns();
        savedProjects = savedProjects.filter(project => project.id !== sewingPatternProject.id);

        try {
            await this.storage.ready();
            await this.storage.set(ConfiguratorService.savedPatternsStorageKey, JSON.stringify(savedProjects));
        } catch (error) {
            localStorage.setItem(ConfiguratorService.savedPatternsStorageKey, JSON.stringify(savedProjects));
        }

        await this.informSubscribers();
    }

    async updateSewingPatternProject(sewingPatternProject: SewingPatternProject) {

        await Filesystem.writeFile({
            path: `configurator/${sewingPatternProject.id}.svg`,
            data: await this.patternTransformService.generateFinalSewingPatternSVG(sewingPatternProject.sewingPattern),
            encoding: FilesystemEncoding.UTF8,
            directory: FilesystemDirectory.Data
        });

        sewingPatternProject.updatedAt = Date.now();

        const savedProjects = await this.getSavedSewingPatterns();
        const index = savedProjects.findIndex(row => row.id === sewingPatternProject.id);

        savedProjects[index] = sewingPatternProject;

        try {
            await this.storage.ready();
            await this.storage.set(ConfiguratorService.savedPatternsStorageKey, JSON.stringify(savedProjects));
        } catch (error) {
            localStorage.setItem(ConfiguratorService.savedPatternsStorageKey, JSON.stringify(savedProjects));
        }

        await this.informSubscribers();
    }

    async savePersonalSewingPattern(name: string, sewingPattern: SewingPatternInterface, sewingPatternSVG: string, id = ulid(), save = true) {
        try {
            try {
                await Filesystem.mkdir({
                    path: 'configurator',
                    directory: FilesystemDirectory.Data,
                    recursive: true
                });
            } catch {}

            const result = await Filesystem.writeFile({
                path: `configurator/${id}.svg`,
                data: sewingPatternSVG,
                encoding: FilesystemEncoding.UTF8,
                directory: FilesystemDirectory.Data
            });

            const project = new SewingPatternProject(name, sewingPattern, result.uri);
            project.id = id;
            project.updatedAt = Date.now();

            if (save) {
                const savedPatterns = await this.getSavedSewingPatterns();
                savedPatterns.push(project);

                try {
                    await this.storage.ready();
                    await this.storage.set(ConfiguratorService.savedPatternsStorageKey, JSON.stringify(savedPatterns));
                } catch (error) {
                    localStorage.setItem(ConfiguratorService.savedPatternsStorageKey, JSON.stringify(savedPatterns));
                }

                await this.informSubscribers();
            }

            return project;

        } catch (error) {
            console.log(error);
        }
    }

    async getSavedSewingPatternById(id: string): Promise<SewingPatternProject|undefined>{
        const savedPatterns = await this.getSavedSewingPatterns();
        return savedPatterns.find(pattern => pattern.id === id);
    }


    async buildPatternDefinitionString(fabrics: FabricInterface[]) {
        const patterns = [];

        const convertBlobToBase64 = (blob) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    resolve(reader.result);
                };
            });
        };

        await Promise.all(fabrics.map(async fabric => {
            if (patterns.findIndex(pattern => pattern.id === fabric.id) !== -1) {
                return;
            }

            const response = await fetch(fabric.path);
            const imageBlob = await response.blob();
            const image = await convertBlobToBase64(imageBlob);

            patterns.push({
                id: fabric.id,
                pattern: `
                    <pattern id="${this.patternPrefix}${fabric.id}" viewBox="0,0,${fabric.width},${fabric.height}" width="330" height="400" preserveAspectRatio="none" patternUnits="userSpaceOnUse">
                        <image x="0" y="0" width="${fabric.width}" height="${fabric.height}" xlink:href="${image}" />
                    </pattern>
                `
            });
        }));

        return patterns.map(pattern => pattern.pattern).join('');
    }

    prepareSvgGroups(svgElement: HTMLElement, areas: PatternAreaInterface[]) {
        const groups = svgElement.querySelectorAll('g,path,rect');

        groups.forEach((group: HTMLElement) => {
            const style = window.getComputedStyle(group);
            areas.forEach(area => {
                if (area.fabric && style.fill === area.color) {
                    group.style.setProperty('fill', `url(#${this.patternPrefix}${area.fabric.id})`);
                }
            });
        });
    }

    async loadSanitizedPatternProjectUrl(project: SewingPatternProject, forceUpdate = false) {
        return this.domSanitizer.bypassSecurityTrustUrl(
            Capacitor.convertFileSrc((await this.loadPngFromSewingPatternProject(project, forceUpdate)) + `${project.updatedAt}`)
        );
    }

    async loadPngFromSewingPatternProject(sewingPatternProject: SewingPatternProject, forceUpdate = false) {
        let result;

        if (!forceUpdate) {
            try {
                result = await Plugins.Filesystem.getUri({
                    path: `configurator/${sewingPatternProject.id}.png`,
                    directory: FilesystemDirectory.External
                });

                return `${result.uri}?${sewingPatternProject.updatedAt}`;
            } catch { }
        }

        await this.saveAsPng(sewingPatternProject);

        result = await Plugins.Filesystem.getUri({
            path: `configurator/${sewingPatternProject.id}.png`,
            directory: FilesystemDirectory.External
        });
        return `${result.uri}?${sewingPatternProject.updatedAt}`;
    }

    /**
     *
     * @param sewingPatternProject
     * @param includeDecoration
     *
     * @returns Promise<string>
     */
    async generateImageFromSewingPatternProject(sewingPatternProject: SewingPatternProject, includeDecoration = false) {
        const canvas = document.createElement('canvas');
        canvas.setAttribute('width', '800');
        canvas.setAttribute('height', '800');

        const ctx = canvas.getContext('2d');

        const patternContent = await this.loadImageFromProject(sewingPatternProject);

        const element = document.createElement('div');

        element.classList.add('opacity-0');
        element.classList.add('absolute');

        element.innerHTML = patternContent;

        document.body.append(element);

        this.prepareSvgGroups(element, sewingPatternProject.sewingPattern.areas);

        let rawSvgString = element.innerHTML;
        let svg = `data:image/svg+xml,${encodeURIComponent(rawSvgString)}`;

        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = async () => {
                rawSvgString = null;
                svg = null;

                canvas.width = includeDecoration ? 800 : image.width;
                canvas.height = includeDecoration ? 800 : image.height;

                setTimeout(async () => {

                    const calculateWidthAndHeight = () => {
                        const sewingPattern = sewingPatternProject.sewingPattern;
                        const targetSize = 780;

                        if (sewingPattern.width > sewingPattern.height) {
                            const targetHeight = (sewingPattern.height / sewingPattern.width) * targetSize;
                            return {
                                width: targetSize,
                                height: targetHeight,
                                left: 10,
                                top: (targetSize - targetHeight) / 2
                            };
                        }
                        const targetWidth = (sewingPattern.width / sewingPattern.height) * targetSize;

                        return {
                            height: targetSize,
                            width: targetWidth,
                            top: 10,
                            left: (targetSize - targetWidth) / 2
                        };
                    };

                    if (includeDecoration) {
                        const { width, height, top, left } = calculateWidthAndHeight();

                        const backgroundImage = await this.loadImage('/assets/images/share-background.png');
                        ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

                        ctx.drawImage(image, left, top, width, height);
                    } else {
                        ctx.drawImage(image, 0, 0);
                    }


                    setTimeout(() => {
                        const png = includeDecoration ? canvas.toDataURL('image/jpeg') : canvas.toDataURL('image/png') as string;

                        image.remove();
                        element.remove();

                        resolve(png);
                    }, 100);

                }, 250);
            };

            image.onerror = (error) => {
                console.error("Error loading image", error);
                reject();
            };

            image.classList.add('opacity-0');
            image.classList.add('absolute');

            image.src = svg;
            document.body.append(image);
        });
    }

    async saveAsPng(sewingPatternProject: SewingPatternProject) {
        const png = (await this.generateImageFromSewingPatternProject(sewingPatternProject)) as string;

        await Plugins.Filesystem.mkdir({
            path: 'configurator',
            directory: FilesystemDirectory.External,
            recursive: true
        }).catch(error => console.error(error));

        await Plugins.Filesystem.deleteFile({
            path: `configurator/${sewingPatternProject.id}.png`,
            directory: FilesystemDirectory.External
        }).catch(error => console.error(error));

        await Plugins.Filesystem.appendFile({
            data: png,
            path: `configurator/${sewingPatternProject.id}.png`,
            directory: FilesystemDirectory.External
        });
    }

    loadImage(source): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                setTimeout(() => resolve(image), 100);
            };
            image.src = source;
        });
    }
}
